import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Link,
  Snackbar,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import Select from '../components/forms/SelectWrapper'
import SubmitButton from '../components/forms/SubmitButtonWrapper'
import Textfield from '../components/forms/TextfieldWrapper'
import OfferMessage from '../components/OfferMessage'
import { Apple } from '../dummy/Apple'
import { Google } from '../dummy/Google'
import { signUpSchema } from '../schema/input'
import SuccessSignUp from '../success/SignUp'
import { useSignup } from './hooks/useSignup'

const specialChars = '!@#$%^&*()_+{}:"<>?|[];\',./'

const SignUp = ({
  handleChange,
}: {
  handleChange: (value: number) => void
}) => {
  const { t } = useTranslation()

  const {
    alertEvent,
    cities,
    countries,
    handleChangeCity,
    handleChangeCountry,
    handleChangeProvince,
    handleClose,
    handleSignUp,
    handleSignupWithApple,
    handleSignupWithGoogle,
    handleTooltipClose,
    initialValues,
    open,
    provinces,
    selectedCityName,
    selectedCountry,
    selectedProvince,
    setOpen,
    tooltipOpen,
    username
  } = useSignup()

  return (
    <>
      <Grid
        container
        sx={{
          p: 2,
        }}
      >
        <Grid item xs={12}>
          <Card>
            <CardHeader
              subheader={
                <Typography
                  align="center"
                  color="text.secondary"
                  sx={{ pt: 2 }}
                  variant="body2"
                >
                  {t('auth.register.description')}
                </Typography>
              }
              title={
                <Typography align="center" color="text.primary" variant="h4">
                  {t('auth.register.welcome')}
                </Typography>
              }
            />
            <CardContent>
              <Grid container rowGap={2}>
                <Grid item xs={12}>
                  <Grid container direction="column" rowGap={1}>
                    <Grid item xs={12}>
                      <Button
                        color="google"
                        disableElevation
                        fullWidth
                        onClick={handleSignupWithGoogle}
                        size="large"
                        startIcon={
                          <SvgIcon>
                            <Google />
                          </SvgIcon>
                        }
                        variant="outlined"
                      >
                        {t('auth.register.sso')}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        color="google"
                        disableElevation
                        fullWidth
                        onClick={handleSignupWithApple}
                        size="large"
                        startIcon={
                          <SvgIcon>
                            <Apple />
                          </SvgIcon>
                        }
                        variant="outlined"
                      >
                        {t('auth.register.apple_sso')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider>
                    <Typography variant="subtitle2">
                      {t('auth.register.option')}
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <Formik
                    initialValues={initialValues}
                    onSubmit={(values) => {
                      handleSignUp(values)
                    }}
                    validationSchema={signUpSchema}
                  >
                    {({ setFieldValue }) => (
                      <Form
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          rowGap: 16,
                        }}
                      >
                        <Textfield
                          label={t('auth.register.firstname')}
                          name="firstName"
                          required
                        />
                        <Textfield
                          label={t('auth.register.lastname')}
                          name="lastName"
                          required
                        />
                        <Textfield
                          label={t('auth.register.email')}
                          name="email"
                          required
                          type="email"
                        />
                        <Tooltip
                          arrow
                          onClose={handleTooltipClose}
                          open={tooltipOpen}
                          title={
                            <Box>
                              <Typography variant="subtitle1">
                                {t('auth.register.policy')}
                              </Typography>
                              <Typography variant="body2">
                                {`${t('auth.register.policy_description')}${specialChars}`}
                              </Typography>
                            </Box>
                          }
                        >
                          <Textfield
                            label={t('auth.register.password')}
                            name="password"
                            required
                            type="password"
                          />
                        </Tooltip>

                        <Select
                          label={t('auth.register.country')}
                          name="country"
                          onChange={(event) => {
                            const country = Number(event.target.value)

                            setFieldValue('country', country)
                            setFieldValue('province', '')
                            setFieldValue('city', '')
                            handleChangeCountry(country)
                          }}
                          options={countries || []}
                        />

                        <Select
                          disabled={!selectedCountry}
                          label={t('auth.register.province')}
                          name="province"
                          onChange={(event) => {
                            const province = Number(event.target.value)

                            setFieldValue('province', province)
                            setFieldValue('city', '')
                            handleChangeProvince(province)
                          }}
                          options={provinces || []}
                        />

                        <Select
                          disabled={!selectedProvince}
                          label={t('auth.register.city')}
                          name="city"
                          onChange={(event) => {
                            const cityId = Number(event.target.value)
                            const selectedCity = cities?.find(
                              (city) => city.id === cityId
                            )
                            setFieldValue('city', cityId)
                            handleChangeCity(selectedCity?.name || '')
                          }}
                          options={cities || []}
                        />

                        <OfferMessage
                          cityName={selectedCityName}
                          countryId={selectedCountry}
                        />

                        <SubmitButton fullWidth>
                          {t('auth.register.cta')}
                        </SubmitButton>
                      </Form>
                    )}
                  </Formik>
                </Grid>
                <Grid item xs={12}>
                  <Typography align="center" variant="caption">
                    {t('auth.register.disclaimer')}{' '}
                    <Link
                      href="https://www.uride.co/company/terms-and-conditions"
                      target="_blank"
                    >
                      {t('auth.register.terms_service')}
                    </Link>
                    ,{' '}
                    <Link
                      href="https://www.uride.co/company/privacy-policy"
                      target="_blank"
                    >
                      {t('auth.register.privacy_policy')}
                    </Link>{' '}
                    {t('auth.register.and')}{' '}
                    <Link
                      href="https://www.uride.co/company/social-code-updated-and-effective"
                      target="_blank"
                    >
                      {t('auth.register.social_conduct')}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={6000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={2}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
      <SuccessSignUp
        email={username}
        handleChange={handleChange}
        open={open}
        setOpen={setOpen}
      />
    </>
  )
}
export default SignUp
